import React from 'react';
import {
  func, objectOf, arrayOf, shape, bool, number, string,
} from 'prop-types';
import cn from 'classnames';
import UploadItem from '../UploadItem';
import { itemShape } from '../UploadItem/UploadItem';
import virtualizedList from '../../helpers/uploadVirtualizedList';

import './styles.scss';

class UploadItemsList extends React.Component {
  state = {
    styles: [],
    wrapperHeight: 0,
    flatGroups: [],
    groups: null,
    totalFilesCount: 0,
    filesCompleted: 0,
    groupsLength: 0,
    topItemIndex: 0,
    bottomItemIndex: 100,
    errorsCount: 0,
    showError: false,
  };

  static getDerivedStateFromProps(props, state) {
    if (
      (props.filesCompleted !== state.filesCompleted
        || props.errorsCount !== state.errorsCount
        || props.totalFilesCount !== state.totalFilesCount
        || Object.keys(props.groups).length !== state.groupsLength
        || props.groups !== state.groups)
      || props.showError !== state.showError
    ) {
      const {
        flatGroups,
        wrapperHeight,
        styles,
      } = virtualizedList(props.groups, props.showCompleted, props.showError, props.isOwner);
      return {
        styles,
        wrapperHeight,
        flatGroups,
        groups: props.groups,
        errorsCount: props.errorsCount,
        totalFilesCount: props.totalFilesCount,
        filesCompleted: props.filesCompleted,
        groupsLength: Object.keys(props.groups).length,
        showError: props.showError,
      };
    }
    return null;
  }

  closestValue = (array, value) => {
    let result;
    let lastDelta;

    array.some((item) => {
      const delta = Math.abs(value - item);
      if (delta >= lastDelta) {
        return true;
      }
      result = item;
      lastDelta = delta;
      return false;
    });
    return result;
  };

  handleScroll = (event) => {
    const { scrollTop } = event.target;
    const holderHeight = event.target.offsetHeight;
    const topItem = this.closestValue(this.state.styles, scrollTop);
    const bottomItem = this.closestValue(this.state.styles, holderHeight + scrollTop);
    this.setState((state) => ({
      topItemIndex: state.styles.findIndex((item) => item === topItem),
      bottomItemIndex: state.styles.findIndex((item) => item === bottomItem),
    }));
  };

  toggleFile = (id, withShift, withAlt) => {
    /** Select only one */
    if (withAlt) {
      const idsToDeselect = this.props.selected.filter((selectedId) => selectedId !== id);
      this.props.onDeselect(idsToDeselect);
      if (idsToDeselect.length === this.props.selected.length) {
        /** If all deselected */
        this.props.onSelect([id]);
      }
      return;
    }
    /** Select range */
    if (withShift) {
      /** Prevent default browser range selection for text */
      document.getSelection().removeAllRanges();
      const {
        selected, groups, onDeselect, onSelect,
      } = this.props;
      const lastSelectedId = selected.length && selected[selected.length - 1];
      const files = Object.values(groups).flat();
      const lastSelectedIndex = lastSelectedId ? files.findIndex((file) => lastSelectedId === file.id) : 0;
      const currentIndex = files.findIndex((file) => file.id === id);
      const start = Math.min(lastSelectedIndex, currentIndex);
      const end = Math.max(lastSelectedIndex, currentIndex);
      const range = files.slice(start, end + 1).map((file) => file.id);
      if (range.every((fileId) => selected.includes(fileId))) {
        /** If all files already selected */
        onDeselect(range);
        return;
      }
      onSelect(range);
    }
    if (this.props.selected.includes(id)) {
      this.props.onDeselect([id]);
      return;
    }
    this.props.onSelect([id]);
  }

  toggleCollection = (path) => {
    const ids = (this.props.groups[path] || []).map(({ id, complete, file }) => (complete || !!file?.['.tag'] ? null : id)).filter(Boolean);
    if (!ids.length) return null; // if collection not found or all files are completed

    /** if all files in collection are selected */
    if (this.props.groups[path].every(({ id, file }) => this.props.selected.includes(id) || !!file?.['.tag'])) {
      return this.props.onDeselect(ids);
    }
    return this.props.onSelect(ids);
  }

  render() {
    const {
      flatGroups, groups, wrapperHeight, topItemIndex, bottomItemIndex,
    } = this.state;
    const {
      retry, remove, selected, showError, isUploadStarted,
    } = this.props;

    return (
      <div
        className={cn('importItemsWrapper', { importItemsWrapperHidden: !wrapperHeight })}
        onScroll={this.handleScroll}
      >
        <div className="importItemsHolder" style={{ height: wrapperHeight }}>
          {flatGroups.slice(topItemIndex, bottomItemIndex + 1).map((item, index) => {
            const isCollection = !!item.groupPath;
            const isItemSelected = selected.includes(item.id) || (isCollection && groups[item.groupPath].every((file) => selected.includes(file.id) || !!file?.file?.['.tag']));

            return (
              <UploadItem.Item
                key={item.id}
                className={!(index % 2) ? 'odd' : null}
                item={item}
                retry={retry}
                remove={remove}
                top={item.top}
                isSelected={isItemSelected}
                toggleSelection={isCollection ? this.toggleCollection : this.toggleFile}
                groups={groups}
                showError={showError}
                isUploadStarted={isUploadStarted}
              />
            );
          })}
        </div>
      </div>
    );
  }
}

UploadItemsList.defaultProps = {
  errorsCount: 0,
  totalFilesCount: 0,
  showCompleted: false,
  filesCompleted: 0,
  selected: [],
  onSelect: Function.prototype,
  onDeselect: Function.prototype,
  retry: Function.prototype,
  showError: false,
  isUploadStarted: false,
  isOwner: false,
};

UploadItemsList.propTypes = {
  groups: objectOf(arrayOf(shape(itemShape))).isRequired,
  selected: arrayOf(string),
  remove: func.isRequired,
  onSelect: func,
  onDeselect: func,
  retry: func,
  filesCompleted: number,
  totalFilesCount: number,
  errorsCount: number,
  showCompleted: bool,
  showError: bool,
  isUploadStarted: bool,
  isOwner: bool,
};

export default UploadItemsList;
